import React, { useState, useEffect } from "react";

const ToggleSwitch = ({ checked, onChange, label, description }) => (
  <div className="settings-item">
    <div className="settings-item-info">
      <div className="settings-item-label">{label}</div>
      {description && (
        <div className="settings-item-description">{description}</div>
      )}
    </div>
    <div className="settings-item-control">
      <div
        className={`toggle-switch ${checked ? "active" : ""}`}
        onClick={() => onChange(!checked)}
      >
        <div className="toggle-switch-slider"></div>
      </div>
    </div>
  </div>
);

const NotificationSettings = ({ settings, onUpdate, showToast }) => {
  const [localSettings, setLocalSettings] = useState({
    email: settings?.email || {},
    push: settings?.push || {},
    inApp: settings?.inApp || {},
  });

  useEffect(() => {
    if (settings) {
      setLocalSettings({
        email: settings.email || {},
        push: settings.push || {},
        inApp: settings.inApp || {},
      });
    }
  }, [settings]);

  const handleToggle = async (type, key, value) => {
    const updated = {
      ...localSettings,
      [type]: {
        ...localSettings[type],
        [key]: value,
      },
    };

    setLocalSettings(updated);

    try {
      await onUpdate(updated);
    } catch (error) {
      // Reverter em caso de erro
      setLocalSettings(localSettings);
    }
  };

  return (
    <div>
      <div className="settings-header">
        <h1>Notificações</h1>
        <p>Escolha como você deseja ser notificado</p>
      </div>

      {/* Notificações por Email */}
      <div className="settings-section">
        <h3 className="settings-section-title">📧 Notificações por Email</h3>
        <p className="settings-section-description">
          Receba atualizações importantes no seu email
        </p>

        <ToggleSwitch
          checked={localSettings.email.enabled !== false}
          onChange={(val) => handleToggle("email", "enabled", val)}
          label="Ativar notificações por email"
          description="Habilitar/desabilitar todas as notificações por email"
        />

        {localSettings.email.enabled !== false && (
          <>
            <ToggleSwitch
              checked={localSettings.email.likes !== false}
              onChange={(val) => handleToggle("email", "likes", val)}
              label="Curtidas"
              description="Quando alguém curtir suas publicações"
            />

            <ToggleSwitch
              checked={localSettings.email.comments !== false}
              onChange={(val) => handleToggle("email", "comments", val)}
              label="Comentários"
              description="Quando alguém comentar em suas publicações"
            />

            <ToggleSwitch
              checked={localSettings.email.mentions !== false}
              onChange={(val) => handleToggle("email", "mentions", val)}
              label="Menções"
              description="Quando alguém mencionar você"
            />

            <ToggleSwitch
              checked={localSettings.email.follows !== false}
              onChange={(val) => handleToggle("email", "follows", val)}
              label="Novos seguidores"
              description="Quando alguém começar a seguir você"
            />

            <ToggleSwitch
              checked={localSettings.email.friendRequests !== false}
              onChange={(val) => handleToggle("email", "friendRequests", val)}
              label="Solicitações de amizade"
              description="Quando alguém enviar uma solicitação de amizade"
            />

            <ToggleSwitch
              checked={localSettings.email.messages !== false}
              onChange={(val) => handleToggle("email", "messages", val)}
              label="Mensagens"
              description="Quando receber novas mensagens"
            />

            <ToggleSwitch
              checked={localSettings.email.liveStreams !== false}
              onChange={(val) => handleToggle("email", "liveStreams", val)}
              label="Lives"
              description="Quando alguém que você segue iniciar uma live"
            />

            <ToggleSwitch
              checked={localSettings.email.newsletter === true}
              onChange={(val) => handleToggle("email", "newsletter", val)}
              label="Newsletter"
              description="Receba notícias e atualizações da plataforma"
            />

            <ToggleSwitch
              checked={localSettings.email.promotions === true}
              onChange={(val) => handleToggle("email", "promotions", val)}
              label="Promoções"
              description="Receba ofertas especiais e promoções"
            />
          </>
        )}
      </div>

      {/* Notificações Push */}
      <div className="settings-section">
        <h3 className="settings-section-title">🔔 Notificações Push</h3>
        <p className="settings-section-description">
          Receba notificações no navegador e dispositivos móveis
        </p>

        <ToggleSwitch
          checked={localSettings.push.enabled !== false}
          onChange={(val) => handleToggle("push", "enabled", val)}
          label="Ativar notificações push"
          description="Habilitar/desabilitar todas as notificações push"
        />

        {localSettings.push.enabled !== false && (
          <>
            <ToggleSwitch
              checked={localSettings.push.likes !== false}
              onChange={(val) => handleToggle("push", "likes", val)}
              label="Curtidas"
            />

            <ToggleSwitch
              checked={localSettings.push.comments !== false}
              onChange={(val) => handleToggle("push", "comments", val)}
              label="Comentários"
            />

            <ToggleSwitch
              checked={localSettings.push.mentions !== false}
              onChange={(val) => handleToggle("push", "mentions", val)}
              label="Menções"
            />

            <ToggleSwitch
              checked={localSettings.push.follows !== false}
              onChange={(val) => handleToggle("push", "follows", val)}
              label="Novos seguidores"
            />

            <ToggleSwitch
              checked={localSettings.push.friendRequests !== false}
              onChange={(val) => handleToggle("push", "friendRequests", val)}
              label="Solicitações de amizade"
            />

            <ToggleSwitch
              checked={localSettings.push.messages !== false}
              onChange={(val) => handleToggle("push", "messages", val)}
              label="Mensagens"
            />

            <ToggleSwitch
              checked={localSettings.push.liveStreams !== false}
              onChange={(val) => handleToggle("push", "liveStreams", val)}
              label="Lives"
            />

            <ToggleSwitch
              checked={localSettings.push.soundEnabled !== false}
              onChange={(val) => handleToggle("push", "soundEnabled", val)}
              label="Som"
              description="Reproduzir som ao receber notificações"
            />

            <ToggleSwitch
              checked={localSettings.push.vibrationEnabled !== false}
              onChange={(val) => handleToggle("push", "vibrationEnabled", val)}
              label="Vibração"
              description="Vibrar ao receber notificações (mobile)"
            />
          </>
        )}
      </div>

      {/* Notificações no App */}
      <div className="settings-section">
        <h3 className="settings-section-title">🔵 Notificações no App</h3>
        <p className="settings-section-description">
          Receba notificações enquanto navega na plataforma
        </p>

        <ToggleSwitch
          checked={localSettings.inApp.enabled !== false}
          onChange={(val) => handleToggle("inApp", "enabled", val)}
          label="Ativar notificações no app"
        />

        {localSettings.inApp.enabled !== false && (
          <>
            <ToggleSwitch
              checked={localSettings.inApp.likes !== false}
              onChange={(val) => handleToggle("inApp", "likes", val)}
              label="Curtidas"
            />

            <ToggleSwitch
              checked={localSettings.inApp.comments !== false}
              onChange={(val) => handleToggle("inApp", "comments", val)}
              label="Comentários"
            />

            <ToggleSwitch
              checked={localSettings.inApp.mentions !== false}
              onChange={(val) => handleToggle("inApp", "mentions", val)}
              label="Menções"
            />

            <ToggleSwitch
              checked={localSettings.inApp.follows !== false}
              onChange={(val) => handleToggle("inApp", "follows", val)}
              label="Novos seguidores"
            />

            <ToggleSwitch
              checked={localSettings.inApp.friendRequests !== false}
              onChange={(val) => handleToggle("inApp", "friendRequests", val)}
              label="Solicitações de amizade"
            />

            <ToggleSwitch
              checked={localSettings.inApp.messages !== false}
              onChange={(val) => handleToggle("inApp", "messages", val)}
              label="Mensagens"
            />
          </>
        )}
      </div>

      <div className="settings-info-card">
        <div className="settings-info-card-title">💡 Dica</div>
        <div className="settings-info-card-text">
          Você pode ajustar suas notificações para reduzir distrações enquanto
          ainda recebe atualizações importantes.
        </div>
      </div>
    </div>
  );
};

export default NotificationSettings;
